import { useEffect, useState } from 'react';
import { PermissionsType, SystemRolesName } from 'src/api';
import { Badge } from 'src/components/atoms';
import { OnRowClickType } from 'src/components/molecules';
import {
  SearchUsersPaginatedQuery,
  useGetOrganisationRolesQuery,
  useSearchUsersPaginatedQuery,
} from 'src/graphql';
import { useTableColumns, useWindowDimensions } from 'src/hooks';
import { useTheme } from 'styled-components';
import { PermissionsContainer } from './Users.styles';
import { getUserPermissionsLabels } from './helpers';

const columns = [
  { label: 'Name', value: 'name' },
  { label: 'Email', value: 'email' },
  { label: 'Role', value: 'role' },
  { label: 'Department', value: 'department' },
  { label: 'Site', value: 'site' },
  { label: 'Country', value: 'country' },
  { label: 'Employee Id', value: 'eid' },
  { label: 'Permissions', value: 'permissions' },
  { label: 'Reporting Status', value: 'status' },
  { label: 'Source', value: 'source' },
];

const sortableColumns = [
  'Name',
  'Email',
  'Role',
  'Department',
  'Site',
  'Country',
  'Employee Id',
  'Permissions',
  'Reporting Status',
  'Source',
];

const userSearchOptions = [
  { value: 'email', label: 'Search By Email' },
  { value: 'name', label: 'Search By Name' },
  {
    value: 'role',
    label: 'Search By Role',
  },
  {
    value: 'site',
    label: 'Search By Site',
  },
  {
    value: 'country',
    label: 'Search By Country',
  },
  {
    value: 'department',
    label: 'Search By Department',
  },
  {
    value: 'eid',
    label: 'Search By Employee ID',
  },
  { value: 'source', label: 'Search By Source' },
  { value: 'INVITED', label: 'Invited', field: 'status' },
  {
    value: 'EXPIRING',
    label: 'Expiring',
    field: 'status',
  },
  {
    value: 'DEACTIVATED',
    label: 'Deactivated',
    field: 'status',
  },
  {
    value: 'NOT INVITED',
    label: 'Not Invited',
    field: 'status',
  },
  { value: PermissionsType.APP, label: 'App Users', field: 'permissions' },
  {
    value: SystemRolesName.ADMIN,
    label: 'Admins',
    field: 'roles',
  },
  {
    value: SystemRolesName.MANAGER,
    label: 'Case Managers',
    field: 'roles',
  },
];

const getAppStatusBadgeColors = (
  status: string,
  theme: ReturnType<typeof useTheme>,
): { bg: string; color: string } => {
  if (theme.isVaultVariant) {
    return {
      bg: theme.newPalette.turquoise.T300,
      color: theme.newPalette.neutral.N0,
    };
  }
  const s = status.toUpperCase();
  if (s === 'INVITED') {
    return {
      bg: theme.atlas.semantic.color.status.success.default,
      color: theme.atlas.semantic.color.status.success.textDefault,
    };
  }
  if (s === 'NOT INVITED') {
    return {
      bg: theme.atlas.semantic.color.status.warning.default,
      color: theme.atlas.semantic.color.type.default,
    };
  }
  if (s === 'EXPIRING') {
    return {
      bg: theme.atlas.core.color.purple[70],
      color: theme.atlas.semantic.color.type.default,
    };
  }
  if (s === 'DEACTIVATED') {
    return {
      bg: theme.atlas.core.color.concrete[90],
      color: theme.atlas.semantic.color.type.default,
    };
  }
  return {
    bg: theme.atlas.semantic.color.action.primary.default,
    color: theme.atlas.semantic.color.action.primary.onPrimary,
  };
};

export const useUsersTable = (rows: number, page: number) => {
  const theme = useTheme();
  const { data: getOrganisationRoles } = useGetOrganisationRolesQuery();

  const organisationRoles = getOrganisationRoles?.getOrganisationRoles;

  const customRoleNames = Object.values(SystemRolesName).filter((value) =>
    value.startsWith('CUSTOM ROLE'),
  );

  const customRoles = organisationRoles?.filter((role) =>
    customRoleNames.includes(role.name as SystemRolesName),
  );

  customRoles?.forEach((role) => {
    const roleName = role.name;

    const alreadyExists = userSearchOptions.find(
      (option) => option.value === roleName,
    );

    const label = role.label ?? '';

    if (!alreadyExists) {
      userSearchOptions.push({
        value: roleName,
        label: label.endsWith('s') ? label : `${label}s`,
        field: 'roles',
      });
    }
  });

  const {
    visibleColumns,
    sortedColumn,
    sortColumns,
    variables,
    search: searchUsers,
    updateVisibleColumns,
    searchValue,
  } = useTableColumns(
    rows,
    page,
    columns,
    [],
    'name',
    'asc',
    userSearchOptions,
  );

  const { width } = useWindowDimensions();

  const padding = 64;
  const navBarWidth = 165;
  const tableWidth = width - padding - navBarWidth;

  const { data, isLoading, isError } = useSearchUsersPaginatedQuery(variables);

  const tableData = (data?.searchUsersPaginated.items ?? []).map((user) => ({
    cellData: [
      user.name,
      user.email,
      user.role,
      user.department,
      user.site,
      user.country,
      user.eid,
      <PermissionsContainer key={user.id}>
        {getUserPermissionsLabels(
          user.SystemRoles as { id: string; name: string; label: string }[],
          theme,
        )}
      </PermissionsContainer>,
      <Badge
        background-color={getAppStatusBadgeColors(user.status, theme).bg}
        color={getAppStatusBadgeColors(user.status, theme).color}
        text={user.status.toLowerCase()}
        variant={'custom'}
        data-testid={`status-${user.status.toLowerCase()}`}
        capitalize
        key={user.id}
      />,
      <Badge
        background-color={
          theme.isVaultVariant
            ? theme.newPalette.turquoise.T300
            : theme.atlas.semantic.color.surface.default
        }
        color={
          theme.isVaultVariant
            ? theme.newPalette.neutral.N0
            : theme.atlas.semantic.color.type.default
        }
        border-color={
          theme.isVaultVariant
            ? undefined
            : theme.atlas.semantic.color.outline.default
        }
        text={(user.source ?? '').replace('_', ' ').toLowerCase()}
        variant={'custom'}
        data-testid={`source-${(user.source ?? '').toLowerCase()}`}
        capitalize
        key={user.id}
      />,
    ],
    row: `${user.id}`,
    data: {
      id: user.id,
      status: user.status,
    },
  }));

  const users = data?.searchUsersPaginated.items ?? [];
  const usersCount = data?.searchUsersPaginated.count;

  const [selectedUsers, setSelectedUsers] = useState<
    SearchUsersPaginatedQuery['searchUsersPaginated']['items']
  >([]);
  const allStatuses = selectedUsers.map((u) => u.status || 'NOT INVITED');
  const [allSelected, setAllSelected] = useState(false);

  const selectUser: OnRowClickType<{
    id: string;
  }> = (id: string) => {
    setSelectedUsers((prev) => {
      if (prev.map((user) => `${user.id}`).includes(id)) {
        return prev.filter((u) => `${u.id}` !== id);
      }
      const currentUser = users.find((user) => `${user.id}` === id);
      if (currentUser) {
        return [...prev, currentUser];
      }
      return [...prev];
    });
  };

  const selectAllUsers = () => {
    if (allSelected) {
      setSelectedUsers([]);
      setAllSelected(false);
    } else {
      setSelectedUsers(users);
      setAllSelected(true);
    }
  };

  // Unselect all on page change
  useEffect(() => {
    setAllSelected(false);
  }, [page]);

  return {
    usersCount,
    organisationRoles,
    isLoading,
    visibleColumns,
    updateVisibleColumns,
    searchUsers,
    tableWidth,
    tableData,
    sortColumns,
    sortedColumn,
    sortableColumns,
    columns,
    isError,
    allSelected,
    allStatuses,
    selectedUsers,
    setSelectedUsers,
    selectUser,
    selectAllUsers,
    searchValue,
    userSearchOptions,
  };
};
